import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const PricingSection = ({ onSelectPlan, onContactSales }) => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const navigate = useNavigate();
  
  const pricingPlans = [
    {
      id: 'single',
      name: 'Single Case',
      description: 'Perfect for one-time analysis needs',
      price: { monthly: 99, annual: 99 },
      features: [
        '1 case credit',
        'AI-powered preliminary report',
        '15-section comprehensive analysis',
        'Image annotation engine',
        'PDF report download',
        'Expert marketplace access'
      ],
      popular: false,
      cta: 'Get Started'
    },
    {
      id: 'starter',
      name: 'Starter',
      description: 'For regular case analysis',
      price: { monthly: 250, annual: 199 },
      features: [
        '3 cases per month',
        'All Single Case features',
        'Priority AI processing',
        'Email Support'
      ],
      popular: true,
      cta: 'Start Free Trial'
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'For high-volume users',
      price: { monthly: 500, annual: 399 },
      features: [
        '10 cases per month',
        'All Starter features',
        'Dedicated account manager'
      ],
      popular: false,
      cta: 'Start Free Trial'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'For organizations with custom needs',
      price: { monthly: 'Custom', annual: 'Custom' },
      features: [
        'Unlimited cases',
        'All Professional features',
        'Custom training sessions'
      ],
      popular: false,
      cta: 'Contact Sales'
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Transparent Pricing for Every Need
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Choose the plan that fits your case volume. All plans include our AI-powered analysis engine.
          </p>
          
          <div className="inline-flex items-center gap-2 p-1 bg-card border border-border rounded-lg">
            <button
              className={`px-6 py-2 rounded-md transition-smooth text-sm md:text-base ${
                billingCycle === 'monthly' ?'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
              }`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-6 py-2 rounded-md transition-smooth text-sm md:text-base ${
                billingCycle === 'annual' ?'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
              }`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <span className="ml-2 px-2 py-0.5 bg-success text-success-foreground rounded text-xs">
                Save 20%
              </span>
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {pricingPlans?.map((plan) => (
            <div
              key={plan?.id}
              className={`relative bg-card border rounded-xl p-6 md:p-8 transition-smooth hover:shadow-elevation-lg ${
                plan?.popular ? 'border-primary shadow-elevation-md' : 'border-border'
              }`}
            >
              {plan?.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1 bg-primary text-primary-foreground rounded-full text-sm font-medium">
                    <Icon name="Star" size={14} />
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">
                  {plan?.name}
                </h3>
                <p className="caption text-muted-foreground">{plan?.description}</p>
              </div>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  {typeof plan?.price?.[billingCycle] === 'number' ? (
                    <>
                      <span className="text-3xl md:text-4xl font-bold text-foreground">
                        ${plan?.price?.[billingCycle]}
                      </span>
                      <span className="text-muted-foreground">
                        /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    </>
                  ) : (
                    <span className="text-3xl md:text-4xl font-bold text-foreground">
                      {plan?.price?.[billingCycle]}
                    </span>
                  )}
                </div>
              </div>
              
              <Button
                variant={plan?.popular ? 'default' : 'outline'}
                fullWidth
                onClick={() => plan?.id === 'enterprise' ? onContactSales() : onSelectPlan(plan?.id)}
                className="mb-6"
              >
                {plan?.cta}
              </Button>
              
              <div className="space-y-3">
                {plan?.features?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Icon name="Check" size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Enterprise CTA */}
        <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-8 text-center border border-border">
          <Icon name="Briefcase" size={48} className="mx-auto mb-4 text-primary" />
          <h3 className="text-2xl font-bold text-foreground mb-2">Enterprise Solutions</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Need high-volume case processing? Get custom pricing, dedicated support, and advanced features tailored to your organization.
          </p>
          <button
            onClick={() => navigate('/enterprise-sales')}
            className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth inline-flex items-center gap-2"
          >
            <Icon name="Mail" size={18} />
            Contact Sales Team
          </button>
        </div>
        
        <div className="mt-12 text-center">
          <p className="caption text-muted-foreground">
            All plans include 256-bit encryption, 2FA security, and Methodology aligned with NFPA 921
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;